<template>
  <div
    v-if="flashcard.id"
    :class="{'asset-tray-open': isAssetTrayOpen, 'prevent-select': shouldBlurQuestion || shouldBlurAnswers}"
    class="flashcard-component-container"
  >
    <scenario-box
      v-if="shouldShowScenarioBox"
      :flashcard="flashcard"
      data-test="scenario-box"
    ></scenario-box>
    <flashcard-question
      v-if="!terminology"
      :class="{ blur: shouldBlurQuestion }"
      :answered="answered"
      :category="category"
      :correct="correct"
      :flashcard="flashcard"
      :metadata="flashcardMetaData"
      data-test="flashcard-question"
      @flashcard-aa-clicked="handleFlashcardAssetAssociationClick"
    />
    <component
      :is="flashcardType"
      :class="{ blur: shouldBlurAnswers }"
      :answered="answered"
      :answer-ids="answerIds"
      :correct="correct"
      :disabled="disabled"
      :flashcard="flashcard"
      :flashcard-state="flashcardState"
      :locked-rationales="lockedRationales"
      :raw-answer-data="rawAnswerData"
      :metadata="flashcardMetaData"
      data-test="flashcard-type"
      @shift-enter="$emit('shift-enter')"
      @set-answer-ids="$emit('set-answer-ids', $event)"
      @set-raw-answer-data="$emit('set-raw-answer-data', $event)"
    />
    <key-takeaway
      v-if="showKeyTakeaway"
      :key-takeaway="flashcard.keyTakeaway"
      data-test="key-takeaway"
    />
    <exam-tip
      v-if="showExamTip"
      :exam-tip="flashcard.examTip"
      data-test="exam-tip"
    />
    <rationale
      v-if="answered && !terminology && flashcard.rationale"
      :category="category"
      :flashcard="flashcard"
      :locked="lockedRationales"
      :sharing-enabled="sharingEnabled"
      data-test="rationale"
      @flashcard-aa-clicked="handleFlashcardAssetAssociationClick"
    />
    <asset-associations-list
      v-if="showAssetAssociation"
      :asset="flashcard"
      context="rationale"
      data-test="asset-associations-list-rationale"
      @flashcard-aa-clicked="$emit('flashcard-aa-clicked', $event)"
    />
    <rating
      v-if="shouldShowRating && !lockedRationales && answered"
      :asset="flashcard"
      :rateable-id="flashcard && flashcard.id"
      :rateable-type="'V3Flashcard'"
      data-test="rating"
    />
    <discussions
      v-if="shouldShowDiscussion"
      :category="category"
      :flashcard="flashcard"
      :user="$store.getters['session/currentUser']"
      data-test="discussions"
    />
    <asset-tray
      :answered="answered"
      :flashcard="flashcard"
      data-test="asset-tray"
    />
  </div>
</template>

<script>

import AssetTray from '@components/flashcards/asset-tray';
import { defineAsyncComponent } from 'vue';

// Question Types
import BlankTextCompletion from '@components/flashcards/types/blankTextCompletion';
import BowTie from '@components/flashcards/types/BowTie';
import ClozeDropdown from '@components/flashcards/types/clozeDropdown';
import DragAndDrop from '@components/flashcards/types/DragAndDrop.vue';
import FractionalNumericEntry from '@components/flashcards/types/fractionalNumericEntry';
import Highlight from '@components/flashcards/types/highlight';
import Hotspot from '@components/flashcards/types/hotspot';
import MatrixGrid from '@components/flashcards/types/matrix-grid';
import MultipleChoice from '@components/flashcards/types/multipleChoice';
import NumericEntry from '@components/flashcards/types/numericEntry';
import Sata from '@components/flashcards/types/sata';
import ScenarioBox from '@components/flashcards/scenario-box';
import Terminology from '@components/flashcards/types/terminology';
import { rateLimitingEnabled } from '@/utils';
import Rating from '@components/singletons/rating';
import RatingClass from '@/classes/rating';

// Question Type - using direct import to help with resolution
import FlashcardQuestion from '@components/flashcards/question';

const OrderedResponse = defineAsyncComponent(() => import('@components/flashcards/types/orderedResponse'));

// Other Components
import AssetAssociationsList from '@components/assetAssociations/list';
import Discussions from '@components/discussions/discussions';
import ExamTip from '@components/flashcards/exam-tip';
import KeyTakeaway from '@components/flashcards/key-takeaway';
import Rationale from '@components/flashcards/rationale';

import MathMlMixin from '@/mixins/mathml';

import { checkRationalesLocked, flashcardType as getFlashcardType } from '@/utils/flashcards';

const FLASHCARD_TYPE_COMPONENT_MAP = {
  'multiple-choice': MultipleChoice,
  'hotspot': Hotspot,
  'ordered-response': OrderedResponse,
  'sata': Sata,
  'blank-text-completion': BlankTextCompletion,
  'highlight': Highlight,
  'cloze-dropdown': ClozeDropdown,
  'bowtie': BowTie,
  'drag-and-drop': DragAndDrop,
  'fractional-numeric-entry': FractionalNumericEntry,
  'matrix-grid': MatrixGrid,
  'numeric-entry': NumericEntry,
  'terminology': Terminology,
};

export default {
  name: 'Flashcard',
  components: {
    AssetTray,
    // Question Types
    BlankTextCompletion,
    Bowtie: BowTie,
    ClozeDropdown,
    DragAndDrop,
    FractionalNumericEntry,
    Highlight,
    Hotspot,
    MatrixGrid,
    MultipleChoice,
    NumericEntry,
    OrderedResponse,
    Rating,
    Sata,
    ScenarioBox,
    Terminology,
    // Other Components
    AssetAssociationsList,
    Discussions,
    ExamTip,
    KeyTakeaway,
    FlashcardQuestion,
    Rationale,
  },
  mixins: [MathMlMixin],
  props: {
    answered: {
      type: Boolean,
      default: false,
    },
    answerIds: {
      type: Array,
    },
    blur: {
      type: Boolean,
      default: false,
    },
    // blur only the answer portion of the flashcard
    blurAnswers: {
      type: Boolean,
      default: true,
    },
    // blur the question text of the flashcard
    blurQuestion: {
      type: Boolean,
      default: true,
    },
    category: {
      type: Object,
    },
    correct: {
      type: Boolean,
      default: false,
    },
    // disable the ability to interact with the answer
    // currently only supported by multiple choice added for ASE
    disabled: {
      type: Boolean,
      default: false,
    },
    flashcard: {
      type: Object,
    },
    flashcardState: {
      type: Object,
    },
    rawAnswerData: {
      type: Object,
    },
    sharingEnabled: {
      type: Boolean,
      default: false,
    },
    showFlashcardMetadata: {
      type: Boolean,
      default: false,
    },
    showRationale: {
      type: Boolean,
      default: false,
    },
    terminology: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['shift-enter', 'set-answer-ids', 'set-raw-answer-data', 'flashcard-aa-clicked'],
  data() {
    return {
      flashcardMetaData: null,
    };
  },
  computed: {
    flashcardType() {
      const type = getFlashcardType(this.flashcard);
      return FLASHCARD_TYPE_COMPONENT_MAP[type] || type;
    },
    isAssetTrayOpen() {
      return this.$store.getters['ui/showAssetTray'];
    },
    lockedRationales() {
      // showRationale should override default behavior
      if (this.showRationale) {
        return false;
      } else if (this.category) {
        return checkRationalesLocked(this.$store, this.category, this.$apptimize);
      }
    },
    shouldBlurAnswers() {
      return this.blur && this.blurAnswers;
    },
    shouldBlurQuestion() {
      return this.blur && this.blurQuestion;
    },
    shouldShowRating() {
      return RatingClass.enabled(this.$store, 'flashcard');
    },
    shouldShowDiscussion() {
      return this.answered && getFlashcardType(this.flashcard) !== 'terminology' && this.$store.getters['discussions/enabled'];
    },
    shouldShowScenarioBox() {
      if( !this.$route ) { return false; }
      let hasScenarioBox = false;
      if (this.$route.name && this.$route.name.includes('scenario')) {
        const grouping = this.$store.getters['groupings/list'].find(grouping => {
          return grouping?.productAssociations.find(pa => pa.categoryId === parseInt(this.$route.params.categoryId));
        });
        const isFlashcardAssociated = grouping?.assetAssociations.find(aa => this.flashcard.id === aa.assetBId);
        if (grouping?.groupingType === 'Scenario Box' && isFlashcardAssociated) {
          hasScenarioBox = true;
        }
      } else if (this.flashcard?.assetAssociations?.length) {
        this.flashcard.assetAssociations.forEach(assetAssociation => {
          if (assetAssociation?.displayType === 'scenario_box') {
            hasScenarioBox = true;
          }
        });
      }
      return hasScenarioBox;
    },
    showExamTip() {
      const examTip = this.flashcard?.examTip;
      return this.answered && examTip && examTip != '';
    },
    showKeyTakeaway() {
      const keyTakeaway = this.flashcard?.keyTakeaway;
      return this.answered && keyTakeaway && keyTakeaway != '';
    },
    showAssetAssociation() {
      return this.answered;
    },
  },
  mounted() {
    this.$watch('flashcard', (newFlashcard, oldFlashcard = {}) => {
      if (newFlashcard && newFlashcard.id !== oldFlashcard.id) {
        if (newFlashcard.assetAssociations?.length > 0) {
          this.$store.dispatch('assetAssociations/assets', {
            assetAssociationIds: newFlashcard.assetAssociations.map(aa => aa.id),
            rateLimited: rateLimitingEnabled({
              apptimize: this.$apptimize,
              store: this.$store,
              analytics: this.$analytics,
            }),
          });
        }
        if (newFlashcard.attachmentIds?.length > 0) {
          this.$store.dispatch('attachments/fetch', { ids: newFlashcard.attachmentIds });
        }
        if (this.showFlashcardMetadata) {
          this.resetMetaData();
          this.fetchMetaData(newFlashcard);
        }
      }
    }, { immediate: true });
  },
  methods: {
    fetchMetaData(flashcard) {
      this.$store.dispatch('flashcardMetadata/fetch', flashcard).then(metadata => {
        this.flashcardMetaData = metadata;
      });
    },
    resetMetaData() {
      this.flashcardMetaData = null;
    },
    handleFlashcardAssetAssociationClick(id) {
      this.$router.push({
        name: 'assetAssociation#show',
        params: {
          assetAssociationId: id,
        },
        query: {
          nav: this.$route.query.nav,
          ids: this.answerIds,
          data: this.rawAnswerData,
        },
      });
    },
  },
};
</script>

<style lang='scss' scoped>
  .flashcard-component-container {
    max-width: rem-calc(568);
    margin: 2rem auto 0;
    &.asset-tray-open {
      padding-bottom: rem-calc(331);
    }
  }
  .blur {
    filter: blur(5px);
    -webkit-user-select: none; /* Safari */
    -ms-user-select: none;     /* IE 10 and IE 11 */
    user-select: none;         /* Standard syntax */
  }
</style>
